import { createFileRoute } from "@tanstack/react-router"
import { parseMarketplaceId } from "@nova-ai/shared"

import { AppShell } from "../components/layout/app-shell"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { MarketplaceSwitch } from "../components/marketplaces/marketplace-switch"
import { PnlSection } from "../components/dashboard/pnl-section"
import { humanizeMarketplaceId } from "../lib/marketplace"
import { useRates } from "../services/use-rates"

/**
 * One marketplace's fee-aware profit & loss (#167), on its own page.
 *
 * It used to sit on the marketplace dashboard between the revenue trajectory and
 * the campaign table. It moved here because it answers a different question on a
 * different clock: the dashboard reads a rolling window an operator re-picks all
 * day, while the statement is a MONTHLY finance view with its own window, and the
 * two pickers side by side invited the reading that one drove the other.
 *
 * No `QueryState` wrapper: `PnlSection` owns its own read, its own loading copy
 * and its own "awaiting source data" state, so a shell gate here would only
 * duplicate them — and it is the whole page, so there is nothing else to hold
 * back.
 */
function MarketplacePnlPage() {
  const { marketplace: raw } = Route.useParams()
  const marketplace = parseMarketplaceId(raw)
  const rates = useRates()

  return (
    <AppShell
      title="Profit & loss"
      description={`Whole-marketplace revenue against every cost — ${humanizeMarketplaceId(marketplace)}.`}
      icon={<MarketplaceMark marketplace={marketplace} />}
      actions={<MarketplaceSwitch marketplace={marketplace} to="/pnl/$marketplace" />}
      fullWidth
    >
      {/* The section names its own currency from the statement read — this page
          holds no dashboard payload to pass one down from. */}
      <PnlSection marketplace={marketplace} rates={rates.data} />
    </AppShell>
  )
}

export const Route = createFileRoute("/pnl/$marketplace/")({ component: MarketplacePnlPage })
